/**
 * WCAG 2.2.2 (Pause, Stop, Hide) — animated GIF freeze/play/pause control.
 * Fixes scan finding "F7: animated images over 5 seconds long that can't be
 * paused or stopped" for every merchant-uploaded GIF, with no per-asset
 * configuration.
 *
 * How it works (two layers):
 *  1. Server-side (preferred): media-with-text renders an EMPTY media area
 *     (aspect ratio kept, play/pause toggle) for any image_picker GIF — the
 *     delivered HTML contains no <img> at all. This script creates the
 *     animated <img> on play and removes it on pause.
 *  2. Runtime fallback: any remaining content <img> pointing to a .gif file is
 *     frozen by default — its first frame is painted onto a canvas that exactly
 *     overlays the image box, with the same play/pause toggle.
 *  - A play/pause toggle gives full keyboard + screen-reader control
 *    (aria-pressed, dynamic aria-label), satisfying 2.2.2 and 4.1.2.
 *  - While frozen, alt text is carried over to the canvas (role="img") so
 *    WCAG 1.1.1 is preserved; the hidden <img> is aria-hidden instead.
 *  - Tiny gifs (< 100px, icons/spinners) and opt-out subtrees
 *    ([data-gif-anim="off"]) are left untouched.
 *  - Without JavaScript the original animated <img> is served unchanged.
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/pause-stop-hide.html
 * @see https://www.tenon.io/test/quality/f7.php (Tenon F7)
 */
(function () {
  "use strict";

  var STATE_ATTR = "data-gif-a11y";
  var OPT_OUT_SELECTOR = '[data-gif-anim="off"]';
  var MIN_DIMENSION = 100; // skip icons / spinners / decorative bits
  var BUTTON_SIZE = 44; // WCAG 2.5.8 target size (AAA-level comfort)
  var BUTTON_MARGIN = 8;

  /* Button labels come from theme translations (locales/*.json via
     snippets/js-variables.liquid); English is only a last-resort fallback. */
  function getLabels() {
    var strings = (window.themeVariables && window.themeVariables.strings) || {};
    return [strings.accessibilityPlayAnimation || "Play animation",
            strings.accessibilityPauseAnimation || "Pause animation"];
  }

  var ICON_PLAY =
    '<svg class="a11y-gif-icon a11y-gif-icon-play" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M8 5v14l11-7z"/></svg>';
  var ICON_PAUSE =
    '<svg class="a11y-gif-icon a11y-gif-icon-pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';

  function injectStyles() {
    if (document.getElementById("a11y-gif-styles")) return;
    var style = document.createElement("style");
    style.id = "a11y-gif-styles";
    style.textContent =
      ".a11y-gif-anchor{position:relative;}" +
      ".a11y-gif-frame{position:absolute;z-index:1;pointer-events:none;max-width:none;}" +
      ".a11y-gif-frame[hidden]{display:none;}" +
      ".a11y-gif-toggle{position:absolute;z-index:2;display:inline-flex;align-items:center;justify-content:center;" +
      "width:" + BUTTON_SIZE + "px;height:" + BUTTON_SIZE + "px;max-width:none;max-height:none;padding:0;margin:0;" +
      "border:0;border-radius:50%;background:rgba(0,0,0,.72);color:#fff;cursor:pointer;" +
      "box-sizing:border-box;transition:background-color .2s ease;}" +
      ".a11y-gif-toggle:hover{background:rgba(0,0,0,.9);}" +
      ".a11y-gif-toggle:focus-visible{outline:3px solid #005bd3;outline-offset:2px;}" +
      ".a11y-gif-toggle .a11y-gif-icon{width:22px;height:22px;fill:currentColor;display:block;}" +
      ".a11y-gif-toggle .a11y-gif-icon-pause{display:none;}" +
      ".a11y-gif-toggle.is-playing .a11y-gif-icon-play{display:none;}" +
      ".a11y-gif-toggle.is-playing .a11y-gif-icon-pause{display:block;}";
    document.head.appendChild(style);
  }

  function isGifImg(img) {
    var url = img.currentSrc || img.src || "";
    if (!url) return false;
    var path = url.split(/[?#]/)[0].toLowerCase();
    return path.slice(-4) === ".gif";
  }

  /**
   * Freeze one animated gif behind a play/pause control.
   */
  function enhance(img) {
    img.setAttribute(STATE_ATTR, "pending");

    // Hide the animated file straight away so no movement ever auto-plays
    // (restored in restoreOriginal() if anything goes wrong).
    var prevInlineVisibility = img.style.visibility;
    img.style.visibility = "hidden";

    var labels = getLabels();
    var host = img.parentElement;
    if (!host) { img.setAttribute(STATE_ATTR, "skip"); return; }

    // A <button> may not live inside another interactive element; climb past
    // EVERY enclosing link/button (nested wrappers, href-less anchors) and
    // mount the toggle next to the outermost one.
    var buttonHost = host;
    var interactiveAncestor = img.closest("a, button");
    if (interactiveAncestor) {
      while (interactiveAncestor.parentElement &&
             interactiveAncestor.parentElement.closest("a, button")) {
        interactiveAncestor = interactiveAncestor.parentElement.closest("a, button");
      }
      if (!interactiveAncestor.parentElement) {
        // Interactive wrapper with no escape hatch: leave the gif untouched.
        img.style.visibility = prevInlineVisibility;
        img.setAttribute(STATE_ATTR, "skip");
        return;
      }
      buttonHost = interactiveAncestor.parentElement;
    }

    if (getComputedStyle(host).position === "static") host.classList.add("a11y-gif-anchor");
    if (buttonHost !== host && getComputedStyle(buttonHost).position === "static") {
      buttonHost.classList.add("a11y-gif-anchor");
    }

    var altText = img.getAttribute("alt") || "";

    var canvas = document.createElement("canvas");
    canvas.className = "a11y-gif-frame";
    canvas.setAttribute("role", "img");
    canvas.setAttribute("aria-label", altText);
    canvas.hidden = true;

    var button = document.createElement("button");
    button.type = "button";
    button.className = "a11y-gif-toggle";
    button.setAttribute("aria-pressed", "false");
    button.setAttribute("aria-label", labels[0]);
    button.innerHTML = ICON_PLAY + ICON_PAUSE;

    host.insertBefore(canvas, img.nextSibling);
    buttonHost.insertBefore(button, buttonHost === host ? canvas : null);

    var playing = false;
    var resizeWatched = false;

    /** Repaint canvas with frame 0 of the gif. */
    var lastDrawnKey = null;
    function drawFirstFrame() {
      try {
        var w = img.naturalWidth || 0;
        var h = img.naturalHeight || 0;
        if (!w || !h) return false;
        // Repeated load events (srcset swaps etc.) for the same source must
        // not blank the canvas: resetting width/height clears the bitmap.
        var key = w + "x" + h + "@" + (img.currentSrc || img.src);
        if (key === lastDrawnKey) return true;
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        lastDrawnKey = key;
        return true;
      } catch (e) {
        return false;
      }
    }

    /** Align canvas + toggle with the (possibly resized) image box. */
    function positionOverlays() {
      var imgBox = img.offsetParent;
      if (!imgBox) return;
      var rect = img.getBoundingClientRect();

      var ctxBox = canvas.offsetParent;
      if (ctxBox) {
        var ctxRect = ctxBox.getBoundingClientRect();
        canvas.style.left = rect.left - ctxRect.left + "px";
        canvas.style.top = rect.top - ctxRect.top + "px";
      }
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";

      var btnCtx = button.offsetParent || ctxBox;
      if (btnCtx) {
        var btnRect = btnCtx.getBoundingClientRect();
        button.style.left = rect.right - btnRect.left - BUTTON_MARGIN - BUTTON_SIZE + "px";
        button.style.top = rect.bottom - btnRect.top - BUTTON_MARGIN - BUTTON_SIZE + "px";
      }
    }

    function setPlaying(on) {
      playing = on;
      if (on) {
        canvas.hidden = true;
        canvas.setAttribute("aria-hidden", "true");
        img.style.visibility = "";
        img.removeAttribute("aria-hidden");
        button.setAttribute("aria-pressed", "true");
        button.setAttribute("aria-label", labels[1]);
        button.classList.add("is-playing");
      } else {
        if (!drawFirstFrame()) { restoreOriginal(); return; }
        positionOverlays();
        canvas.hidden = false;
        canvas.removeAttribute("aria-hidden");
        img.style.visibility = "hidden";
        img.setAttribute("aria-hidden", "true");
        button.setAttribute("aria-pressed", "false");
        button.setAttribute("aria-label", labels[0]);
        button.classList.remove("is-playing");
      }
    }

    button.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      setPlaying(!playing);
    });

    function restoreOriginal() {
      img.style.visibility = prevInlineVisibility;
      img.removeAttribute("aria-hidden");
      canvas.remove();
      button.remove();
      img.setAttribute(STATE_ATTR, "skip");
    }

    /** Keep the frozen canvas + toggle aligned with the image box. */
    function watchResize() {
      if (resizeWatched) return;
      resizeWatched = true;
      if (typeof ResizeObserver !== "undefined") {
        new ResizeObserver(function () {
          window.requestAnimationFrame(positionOverlays);
        }).observe(img);
      }
      window.addEventListener("resize", function () {
        window.requestAnimationFrame(positionOverlays);
      }, { passive: true });
    }

    img.addEventListener("load", function () {
      // Late-loaded (lazy) or re-swapped images: re-freeze on the new content.
      if (playing) return;
      var size = Math.max(img.naturalWidth, img.naturalHeight);
      if (size && size < MIN_DIMENSION) { restoreOriginal(); return; }
      setPlaying(false);
      watchResize();
    });

    // Broken image (or aborted download): bring back the browser's own
    // broken-image handling instead of a permanently blank box.
    img.addEventListener("error", restoreOriginal);

    if (img.complete && img.naturalWidth > 0) {
      var size = Math.max(img.naturalWidth, img.naturalHeight);
      if (size < MIN_DIMENSION) { restoreOriginal(); return; }
      setPlaying(false);
      watchResize();
    }

    if (img.getAttribute(STATE_ATTR) !== "skip") {
      img.setAttribute(STATE_ATTR, "enhanced");
    }
  }

  function scan(root) {
    if (!root || root.nodeType !== Node.ELEMENT_NODE) return;
    var imgs = root.matches && root.matches("img") ? [root] : [];
    if (root.querySelectorAll) {
      var found = root.querySelectorAll("img");
      for (var i = 0; i < found.length; i++) imgs.push(found[i]);
    }
    for (var j = 0; j < imgs.length; j++) {
      var img = imgs[j];
      if (img.hasAttribute(STATE_ATTR) || !isGifImg(img)) continue;
      if (img.closest(OPT_OUT_SELECTOR)) { img.setAttribute(STATE_ATTR, "skip"); continue; }
      if (img.closest(".gif-player")) { img.setAttribute(STATE_ATTR, "skip"); continue; }
      if (img.closest("noscript")) continue;
      enhance(img);
    }
  }

  function setupObserver() {
    var scheduled = false;
    var observer = new MutationObserver(function (mutations) {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) scan(added[j]);
        }
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * Server-rendered empty gif players (media-with-text.liquid renders one for
   * any image_picker GIF): the HTML ships an empty media area that keeps
   * the gif's aspect ratio plus a play toggle; the animated <img> is created
   * only when the user activates the toggle and removed on pause, so the
   * resting page contains no animated gif at all.
   * Document-level delegation survives theme editor re-renders.
   */
  function setupGifPlayers() {
    document.documentElement.classList.add("gif-player-js");

    document.addEventListener("click", function (event) {
      var button = event.target && event.target.closest ? event.target.closest(".gif-player__toggle") : null;
      if (!button) return;

      var player = button.closest(".gif-player");
      if (!player) return;

      event.preventDefault();
      event.stopPropagation();

      var playing = button.classList.toggle("is-playing");
      button.setAttribute("aria-pressed", playing ? "true" : "false");
      var labels = getLabels();
      button.setAttribute("aria-label", playing ? labels[1] : labels[0]);
      var poster = player.querySelector(".gif-player__poster");

      if (playing) {
        var img = player.querySelector("img:not(.gif-player__poster)");
        if (!img) {
          img = document.createElement("img");
          img.alt = player.getAttribute("data-gif-alt") || "";
          img.setAttribute(STATE_ATTR, "skip"); // user-initiated, exempt from runtime freeze
          // Keep the poster visible while the gif downloads; hide it on load.
          img.addEventListener("load", function () {
            if (img.isConnected && poster) poster.style.display = "none";
          });
          img.addEventListener("error", function () {
            if (!img.isConnected) return;
            img.remove();
            button.classList.remove("is-playing");
            button.setAttribute("aria-pressed", "false");
            button.setAttribute("aria-label", labels[0]);
            if (poster) poster.style.display = "";
          });
          player.insertBefore(img, player.querySelector(".gif-player__toggle"));
        }
        img.src = player.getAttribute("data-gif-src");
        if (img.complete && img.naturalWidth > 0 && poster) poster.style.display = "none";
      } else {
        var current = player.querySelector("img:not(.gif-player__poster)");
        if (current) current.remove();
        if (poster) poster.style.display = "";
      }
    });
  }

  function init() {
    injectStyles();
    scan(document.body || document.documentElement);
    setupObserver();
    setupGifPlayers();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
